import React, { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from 'react';
import { Bot, ChevronRight, Clock, Gamepad2, Globe2, Layers3, Lock, Search, Sparkles, Swords, Wand2 } from 'lucide-react';
const PlayGameV2 = lazy(() => import('../playmat-v2/PlayGameV2'));
import {
  connectSocket,
  disconnectSocket,
  startBotGame,
  sendGameAction,
  sendMulligan,
  selectBattlefield,
  chooseStartingPlayer,
  onGameStateUpdate,
  onGameError,
  onConnectionStatus,
  onOpponentDisconnected,
  onOpponentForfeit,
  rejoinBotGame,
} from '../services/socketService';
import { useAuth } from '../contexts/AuthContext';
import { getLocalDecks } from '../utils/localDeckStorage';
import { getCardImageUrl } from '../utils/imageCache';
import { matchFromGameResult, recordCompletedMatch } from '../utils/matchHistory';
import { mergeGameStateDelta } from './playStateDelta';

const PLAY_MODES = [
  {
    id: 'bot',
    title: 'Vs Bot',
    subtitle: 'Partita rapida contro AI locale',
    status: 'Disponibile',
    icon: Bot,
    enabled: true,
  },
  {
    id: 'online',
    title: 'Online',
    subtitle: 'Lobby PvP e inviti privati',
    status: 'In arrivo',
    icon: Globe2,
    enabled: false,
  },
  {
    id: 'sandbox',
    title: 'Sandbox',
    subtitle: 'Test libero di mani e board',
    status: 'In arrivo',
    icon: Wand2,
    enabled: false,
  },
  {
    id: 'tutorial',
    title: 'Tutorial',
    subtitle: 'Impara sequenza turno e combattimento',
    status: 'In arrivo',
    icon: Sparkles,
    enabled: false,
  },
];

const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';

function isDesktopViewport() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
}

const countCards = (cards = []) => cards.reduce((sum, card) => sum + (card.count || 1), 0);

const getDeckMainCount = (deck) => countCards(deck?.mainCards || []) + (deck?.chosenChampion ? 1 : 0);
const getDeckRuneCount = (deck) => countCards(deck?.runes || []);
const getDeckBattlefieldCount = (deck) => deck?.battlefields?.length || 0;

const getDeckDomains = (deck) => {
  const tags = deck?.legend?.tags || deck?.chosenChampion?.tags || [];
  return tags.filter(Boolean).slice(0, 3);
};

const getDeckSearchValue = (deck) => [
  deck?.name,
  deck?.description,
  deck?.legend?.name,
  deck?.chosenChampion?.name,
  ...getDeckDomains(deck),
].filter(Boolean).join(' ').toLowerCase();

function ConnectionBanner({ status }) {
  if (status === 'connected' || status === 'unknown') return null;
  const cfg = {
    disconnected: { bg: 'bg-red-900/90',    text: 'Connessione persa. Riconnessione in corso…' },
    reconnecting:  { bg: 'bg-yellow-900/90', text: '⟳ Riconnessione in corso…' },
    failed:        { bg: 'bg-red-950/95',    text: '✕ Impossibile riconnettersi. Ricarica la pagina.' },
  };
  const { bg, text } = cfg[status] || cfg.disconnected;
  return (
    <div className={`absolute top-0 left-0 right-0 z-[60] ${bg} text-white text-xs text-center py-1.5 font-semibold pointer-events-none`}>
      {text}
    </div>
  );
}

function ErrorToast({ error, onDismiss }) {
  if (!error) return null;
  return (
    <div className="absolute top-8 left-1/2 -translate-x-1/2 z-[55] bg-red-900/95 border border-red-600/60 text-red-100 text-xs px-4 py-2 rounded shadow-lg flex items-center gap-3 max-w-sm">
      <span className="flex-1">{error}</span>
      <button onClick={onDismiss} className="shrink-0 text-red-300 hover:text-white" aria-label="Chiudi">✕</button>
    </div>
  );
}

function OpponentEventBanner({ event, onDismiss }) {
  if (!event) return null;
  const isForfeit = event.type === 'forfeit';
  return (
    <div className={`absolute top-8 left-1/2 -translate-x-1/2 z-[55] ${isForfeit ? 'bg-green-900/95 border-green-600/60 text-green-100' : 'bg-yellow-900/95 border-yellow-600/60 text-yellow-100'} border text-xs px-4 py-2 rounded shadow-lg flex items-center gap-3 max-w-sm`}>
      <span className="flex-1">{event.message}</span>
      <button onClick={onDismiss} className="shrink-0 hover:opacity-70" aria-label="Chiudi">✕</button>
    </div>
  );
}

export default function Play() {
  const { user } = useAuth();
  const [isDesktop, setIsDesktop] = useState(isDesktopViewport);
  const [view, setView] = useState('lobby'); // 'lobby' | 'mulligan' | 'game'
  const [mulliganHand, setMulliganHand] = useState([]);
  const [mulliganSelected, setMulliganSelected] = useState([]);
  const [decks, setDecks] = useState([]);
  const [decksLoading, setDecksLoading] = useState(false);
  const [selectedModeId, setSelectedModeId] = useState('bot');
  const [selectedDeckId, setSelectedDeckId] = useState(null);
  const [deckSearch, setDeckSearch] = useState('');
  const [gameState, setGameState] = useState(null);
  const [possibleActions, setPossibleActions] = useState([]);
  const [gameResult, setGameResult] = useState(null);
  const [error, setError] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('unknown'); // 'connected'|'disconnected'|'reconnecting'|'failed'
  const [opponentEvent, setOpponentEvent] = useState(null); // { type: 'disconnected'|'forfeit', message: string }
  const errorTimerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [startingRoll, setStartingRoll] = useState(null);
  const [battlefieldOptions, setBattlefieldOptions] = useState([]);
  const [selectedBattlefieldIndex, setSelectedBattlefieldIndex] = useState(null);
  const activeDeckRef = useRef(null);
  const activeMatchIdRef = useRef(null);
  const botStartingChoiceSentRef = useRef(false);
  const showError = useCallback((msg) => {
    setError(msg);
    clearTimeout(errorTimerRef.current);
    errorTimerRef.current = setTimeout(() => setError(''), 6000);
  }, []);

  // Mirror of gameState for use inside the socket listener (whose useEffect deps
  // don't include gameState). Updated SYNCHRONOUSLY in the callback below — a
  // deferred useEffect was too late: rapid socket events (card play → chain → bot
  // reaction) within one paint cycle read a stale ref, so mergeDelta ran on a
  // null base and dropped turnState → black screen.
  const gameStateRef = useRef(null);
  const selectedMode = PLAY_MODES.find((mode) => mode.id === selectedModeId) || PLAY_MODES[0];

  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const update = () => setIsDesktop(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, []);

  const selectedDeck = useMemo(
    () => decks.find((deck) => (deck.id || deck.localId) === selectedDeckId) || decks[0] || null,
    [decks, selectedDeckId]
  );

  const filteredDecks = useMemo(() => {
    const query = deckSearch.trim().toLowerCase();
    if (!query) return decks;
    return decks.filter((deck) => getDeckSearchValue(deck).includes(query));
  }, [decks, deckSearch]);

  const selectedDeckImage = getCardImageUrl(selectedDeck?.legend) || getCardImageUrl(selectedDeck?.chosenChampion);
  const selectedDeckDomains = getDeckDomains(selectedDeck);

  const normalizeGameState = useCallback((state) => {
    if (!state) return state;
    const normalizePlayer = (player = {}) => {
      const deckSize = player.deckSize ?? player.mainDeckSize ?? player.deckCount ?? (
        Array.isArray(player.mainDeck) ? player.mainDeck.length : undefined
      );
      const runeDeckSize = player.runeDeckSize ?? player.runesDeckSize ?? player.runeDeckCount ?? (
        Array.isArray(player.runeDeck) ? player.runeDeck.length : undefined
      );
      return {
        ...player,
        deckSize,
        runeDeckSize,
        base: {
          units: player.base?.units || [],
          gear: player.base?.gear || [],
          runes: player.base?.runes || [],
        },
        resources: player.resources || { energy: 0, power: {} },
        discardPile: player.discardPile || player.graveyard || [],
      };
    };

    return {
      ...state,
      human: normalizePlayer(state.human),
      bot: normalizePlayer(state.bot),
      board: {
        ...state.board,
        battlefields: Array.isArray(state.board?.battlefields) ? state.board.battlefields : [],
      },
      turnState: state.turnState || { currentPlayer: 0, turnPhase: 'action', gamePhase: 'neutral_open', priority: 0, turnNumber: 1 },
      chain: state.chain || [],
    };
  }, []);

  // ── Delta merge ───────────────────────────────────────────────────────────────
  // Merges a delta object into the current gameState. Battlefield arrays are
  // merged by index; player deltas are partial so static public fields
  // (champion/legend/deck counts) survive hand/resource updates.
  const mergeDelta = useCallback((state, delta) => {
    return mergeGameStateDelta(state, delta);
  }, []);

  const createSelectionPreviewState = useCallback((deck) => {
    if (!deck) return null;
    const mainDeckSize = (deck.mainCards || []).reduce((sum, card) => sum + (card.count || 1), 0);
    const runeDeckSize = (deck.runes || []).reduce((sum, card) => sum + (card.count || 1), 0);
    const emptyBase = { units: [], gear: [], runes: [] };
    const emptyResources = { energy: 0, power: {} };

    return {
      human: {
        hand: [],
        base: emptyBase,
        resources: emptyResources,
        legend: deck.legend || null,
        champion: deck.chosenChampion || null,
        points: 0,
        deckSize: mainDeckSize,
        runeDeckSize,
        discardPile: [],
      },
      bot: {
        handSize: 0,
        base: emptyBase,
        resources: emptyResources,
        legend: deck.legend || null,
        champion: deck.chosenChampion || null,
        points: 0,
        deckSize: mainDeckSize,
        runeDeckSize,
        discardPile: [],
      },
      board: { battlefields: [] },
      turnState: { currentPlayer: 0, turnPhase: 'setup', gamePhase: 'battlefield_selection', priority: 0, turnNumber: 0 },
      chain: [],
      victoryScore: 8,
    };
  }, []);

  // Normalize a server deck (cards nested under .cards) to the flat shape the game engine expects
  const normalizeServerDeck = (deck) => {
    const c = deck.cards || {};
    return {
      id: deck.id,
      name: deck.name,
      description: deck.description,
      legend: c.legend || null,
      mainCards: c.mainCards || [],
      runes: c.runes || [],
      battlefields: c.battlefields || [],
      chosenChampion: c.chosenChampion || null,
    };
  };

  // Load decks: API first (authenticated), fallback to localStorage
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (user && token) {
      setDecksLoading(true);
      fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/decks`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => r.json())
        .then((data) => {
          const serverDecks = Array.isArray(data) ? data : (data.decks || []);
          if (serverDecks.length > 0) {
            setDecks(serverDecks.map(normalizeServerDeck));
          } else {
            setDecks(getLocalDecks());
          }
        })
        .catch(() => setDecks(getLocalDecks()))
        .finally(() => setDecksLoading(false));
    } else {
      setDecks(getLocalDecks());
    }
  }, [user]);

  useEffect(() => {
    if (decks.length === 0) {
      setSelectedDeckId(null);
      return;
    }
    const selectedStillExists = decks.some((deck) => (deck.id || deck.localId) === selectedDeckId);
    if (!selectedStillExists) {
      setSelectedDeckId(decks[0].id || decks[0].localId);
    }
  }, [decks, selectedDeckId]);

  // Connect socket and register listeners once user is authenticated
  useEffect(() => {
    if (!isDesktop) {
      disconnectSocket();
      return undefined;
    }
    if (!user) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    connectSocket(token);
    const off1 = onGameStateUpdate((data) => {
      if (data.phase === 'starting_roll') {
        const sr = normalizeGameState(data.gameState || createSelectionPreviewState(activeDeckRef.current));
        gameStateRef.current = sr;
        setGameState(sr);
        setStartingRoll(data.startingRoll || null);
        botStartingChoiceSentRef.current = false;
        setBattlefieldOptions([]);
        setSelectedBattlefieldIndex(null);
        setMulliganSelected([]);
        setView('starting_roll');
        setLoading(false);
        return;
      }
      // Phase: battlefield selection
      if (data.phase === 'battlefield_selection') {
        const bs = normalizeGameState(data.gameState || createSelectionPreviewState(activeDeckRef.current));
        gameStateRef.current = bs;
        setGameState(bs);
        setStartingRoll(null);
        botStartingChoiceSentRef.current = false;
        setBattlefieldOptions(data.options || []);
        setSelectedBattlefieldIndex(null);
        setView('battlefield_selection');
        setLoading(false);
        return;
      }
      // Phase: mulligan (server signals via pendingMulligan on gameState)
      if (data.phase === 'mulligan' || data.gameState?.pendingMulligan) {
        const mg = normalizeGameState(data.gameState);
        gameStateRef.current = mg;
        setGameState(mg);
        setStartingRoll(null);
        botStartingChoiceSentRef.current = false;
        setMulliganHand(data.mulligan?.hand || []);
        setMulliganSelected([]);
        setBattlefieldOptions([]);
        setSelectedBattlefieldIndex(null);
        setView('mulligan');
        setLoading(false);
        return;
      }
      // Delta update: merge into current gameState (shallow)
      let nextGameState;
      if (data.delta && !data.gameState) {
        nextGameState = mergeDelta(gameStateRef.current, data.delta);
      } else if (data.gameState) {
        nextGameState = normalizeGameState(data.gameState);
      } else {
        return;
      }
      gameStateRef.current = nextGameState;
      setGameState(nextGameState);
      if (Array.isArray(data.possibleActions)) {
        setPossibleActions(data.possibleActions);
      }
      if (nextGameState) {
        setStartingRoll(null);
        botStartingChoiceSentRef.current = false;
        setMulliganSelected([]);
        setBattlefieldOptions([]);
        setSelectedBattlefieldIndex(null);
        setView('game');
      }
      if (data.gameResult) {
        setGameResult(data.gameResult);
        if (data.gameResult.gameOver && activeMatchIdRef.current) {
          const completedMatch = matchFromGameResult(data.gameResult, {
            id: activeMatchIdRef.current,
            completedAt: new Date().toISOString(),
            opponent: 'Bot',
            mode: 'bot',
            deckName: activeDeckRef.current?.name || '',
          });
          if (completedMatch) recordCompletedMatch(completedMatch);
          activeMatchIdRef.current = null;
        }
      }
      setError('');
      setLoading(false);
    });
    const off2 = onGameError((data) => {
      showError(data.message || 'Errore di gioco');
      setLoading(false);
    });
    const off3 = onConnectionStatus((status) => {
      setConnectionStatus(status);
      if (status === 'connected') {
        // On reconnect, attempt to rejoin an active bot session
        rejoinBotGame();
      }
    });
    const off4 = onOpponentDisconnected((data) => {
      setOpponentEvent({ type: 'disconnected', message: data.message || 'Avversario disconnesso. In attesa di riconnessione...' });
    });
    const off5 = onOpponentForfeit((data) => {
      setOpponentEvent({ type: 'forfeit', message: data.message || 'Avversario non riconnesso. Hai vinto!' });
    });
    return () => { off1(); off2(); off3(); off4(); off5(); };
  }, [user, isDesktop, createSelectionPreviewState, normalizeGameState, showError]);

  useEffect(() => {
    if (view !== 'starting_roll' || startingRoll?.winnerId !== 1 || loading || botStartingChoiceSentRef.current) return;
    botStartingChoiceSentRef.current = true;
    const timer = setTimeout(() => {
      setLoading(true);
      chooseStartingPlayer(1);
    }, 4500);
    return () => clearTimeout(timer);
  }, [view, startingRoll?.winnerId, loading]);

  const handleStartBotGame = (deck) => {
    setError('');
    setLoading(true);
    setGameResult(null);
    setStartingRoll(null);
    botStartingChoiceSentRef.current = false;
    activeDeckRef.current = deck;
    activeMatchIdRef.current = globalThis.crypto?.randomUUID?.()
      || `bot-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setBattlefieldOptions([]);
    setSelectedBattlefieldIndex(null);
    const params = new URLSearchParams(window.location.search);
    const seed = Number(params.get('seed'));
    startBotGame(deck, Number.isInteger(seed) ? seed : undefined);
    // view will switch to 'mulligan' when server responds with pendingMulligan:true
  };

  const toggleMulliganCard = (index) => {
    setMulliganSelected((prev) =>
      prev.includes(index)
        ? prev.filter((i) => i !== index)
        : prev.length < 2 ? [...prev, index] : prev
    );
  };

  const handleConfirmMulligan = () => {
    setError('');
    setLoading(true);
    sendMulligan(mulliganSelected);
  };

  const handleSelectBattlefield = (index) => {
    setError('');
    setLoading(true);
    setSelectedBattlefieldIndex(index);
    selectBattlefield(index);
  };

  const handleChooseStartingPlayer = (playerId) => {
    setError('');
    setLoading(true);
    chooseStartingPlayer(playerId);
  };

  const handleAction = useCallback((action, params) => {
    setError('');
    setLoading(true);
    sendGameAction(action, params);
  }, []);

  const handleBackToLobby = () => {
    setView('lobby');
    gameStateRef.current = null;
    setGameState(null);
    setPossibleActions([]);
    setGameResult(null);
    setStartingRoll(null);
    botStartingChoiceSentRef.current = false;
    activeDeckRef.current = null;
    activeMatchIdRef.current = null;
    setBattlefieldOptions([]);
    setSelectedBattlefieldIndex(null);
    setMulliganHand([]);
    setMulliganSelected([]);
  };

  if (!isDesktop) {
    return (
      <main className="grid h-[calc(100vh-4rem)] place-items-center bg-background p-6 text-foreground" data-testid="play-mobile-unsupported">
        <section className="w-full max-w-md border border-border bg-card/70 p-6 text-center shadow-2xl shadow-black/40">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-info">Playmat desktop</div>
          <h1 className="mt-3 text-xl font-bold">Serve uno schermo più grande</h1>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Per ora il gameplay è supportato da 1024 px in su. Usa un computer o un tablet in orizzontale.
          </p>
          <a href="/" className="mt-5 inline-flex border border-info/60 bg-info/15 px-4 py-2 text-sm font-semibold text-info focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            Torna alla home
          </a>
        </section>
      </main>
    );
  }

  if (!user) {
    return (
      <div className="h-[calc(100vh-4rem)] px-6 py-8">
        <div className="mx-auto flex h-full max-w-5xl items-center justify-center">
          <div className="w-full max-w-xl border border-border/60 bg-card/70 p-8 text-center shadow-2xl shadow-black/20">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded bg-primary/15 text-primary">
              <Lock className="h-7 w-7" />
            </div>
            <h1 className="font-display text-3xl font-bold">Play</h1>
            <p className="mt-3 text-muted-foreground">
              Devi effettuare il login per accedere alle modalità di gioco e ai tuoi mazzi salvati.
            </p>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('openLoginModal'))}
              className="mt-6 inline-flex h-11 items-center justify-center gap-2 rounded bg-primary px-5 text-sm font-bold text-primary-foreground transition hover:bg-primary/90"
            >
              Accedi
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  const gameOverlayBanners = (
    <>
      <ConnectionBanner status={connectionStatus} />
      <ErrorToast error={error} onDismiss={() => setError('')} />
      <OpponentEventBanner event={opponentEvent} onDismiss={() => setOpponentEvent(null)} />
    </>
  );
  if (view === 'starting_roll') {
    return (
      <div className="fixed inset-0 z-50 bg-gray-900">
        {gameOverlayBanners}
        <Suspense fallback={null}>
          <PlayGameV2
            gameState={gameState}
            possibleActions={[]}
            onAction={handleAction}
            gameResult={gameResult}
            onBack={handleBackToLobby}
            startingRoll={{
              active: true,
              ...startingRoll,
              loading,
              onChoose: handleChooseStartingPlayer,
            }}
          />
        </Suspense>
      </div>
    );
  }

  if (view === 'battlefield_selection') {
    return (
      <div className="fixed inset-0 z-50 bg-gray-900">
        {gameOverlayBanners}
        <Suspense fallback={null}>
          <PlayGameV2
            gameState={gameState}
            possibleActions={[]}
            onAction={handleAction}
            gameResult={gameResult}
            onBack={handleBackToLobby}
            battlefieldSelection={{
              active: true,
              options: battlefieldOptions,
              selectedIndex: selectedBattlefieldIndex,
              loading,
              onSelect: handleSelectBattlefield,
            }}
          />
        </Suspense>
      </div>
    );
  }

  if (view === 'mulligan') {
    return (
      <div className="fixed inset-0 z-50 bg-gray-900">
        {gameOverlayBanners}
        <Suspense fallback={null}>
          <PlayGameV2
            gameState={gameState}
            possibleActions={[]}
            onAction={handleAction}
            gameResult={gameResult}
            onBack={handleBackToLobby}
            mulliganSelection={{
              active: true,
              hand: mulliganHand,
              selectedIndices: mulliganSelected,
              loading,
              onToggle: toggleMulliganCard,
              onConfirm: handleConfirmMulligan,
            }}
          />
        </Suspense>
      </div>
    );
  }

  if (view === 'game') {
    return (
      <div className="fixed inset-0 z-50 bg-gray-900">
        {gameOverlayBanners}
        <Suspense fallback={null}>
          <PlayGameV2
            gameState={gameState}
            possibleActions={possibleActions}
            onAction={handleAction}
            gameResult={gameResult}
            onBack={handleBackToLobby}
          />
        </Suspense>
      </div>
    );
  }

  // Lobby view: mode and deck selection
  return (
    <div className="h-[calc(100vh-4rem)] overflow-hidden bg-background px-6 py-6">
      <div className="mx-auto grid h-full max-w-[1500px] grid-cols-[300px_minmax(420px,1fr)_390px] gap-5">
        <aside className="flex min-h-0 flex-col border border-emerald-950/60 bg-background p-4 shadow-xl shadow-black/20">
          <div className="mb-5">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              <Gamepad2 className="h-4 w-4" />
              Play
            </div>
            <h1 className="mt-2 font-display text-3xl font-bold">Scegli modalità</h1>
          </div>

          <div className="space-y-2">
            {PLAY_MODES.map((mode) => {
              const Icon = mode.icon;
              const active = selectedModeId === mode.id;
              return (
                <button
                  key={mode.id}
                  type="button"
                  onClick={() => setSelectedModeId(mode.id)}
                  className={`w-full border p-4 text-left transition ${
                    active
                      ? 'border-primary/70 bg-primary/10 text-foreground'
                      : 'border-emerald-950/50 bg-[#0c100e]/75 text-muted-foreground hover:border-emerald-800/60 hover:bg-[#141b17]'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded ${active ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-bold text-foreground">{mode.title}</div>
                        <span className={`shrink-0 text-[11px] font-bold uppercase ${mode.enabled ? 'text-emerald-400' : 'text-amber-300'}`}>
                          {mode.status}
                        </span>
                      </div>
                      <div className="mt-1 text-sm leading-snug">{mode.subtitle}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-auto border-t border-border/50 pt-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-2 font-medium text-foreground">
              <Clock className="h-4 w-4 text-primary" />
              Stato modalità
            </div>
            <p className="mt-2 leading-relaxed">
              Ora puoi avviare partite contro bot. Le altre modalità sono pronte nella UI per essere collegate quando il backend sarà disponibile.
            </p>
          </div>
        </aside>

        <section className="flex min-h-0 flex-col border border-emerald-950/60 bg-background shadow-xl shadow-black/20">
          <div className="border-b border-border/60 p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-2xl font-bold">Mazzi disponibili</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Seleziona un mazzo, controlla il riepilogo e avvia la modalità scelta.
                </p>
              </div>
              <div className="flex h-10 items-center gap-2 border border-emerald-950/60 bg-[#0c100e]/80 px-3">
                <Search className="h-4 w-4 text-muted-foreground" />
                <input
                  type="search"
                  value={deckSearch}
                  onChange={(event) => setDeckSearch(event.target.value)}
                  placeholder="Cerca mazzo, legend, champion"
                  className="h-full w-64 bg-transparent text-sm outline-none placeholder:text-muted-foreground/70"
                />
              </div>
            </div>
            {error && (
              <div className="mt-4 border border-red-500/40 bg-red-950/40 px-3 py-2 text-sm text-red-200">
                {error}
              </div>
            )}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-5">
            {decksLoading ? (
              <div className="grid grid-cols-2 gap-3">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="h-28 animate-pulse border border-emerald-950/50 bg-[#0c100e]/70" />
                ))}
              </div>
            ) : decks.length === 0 ? (
              <div className="flex h-full items-center justify-center">
                <div className="max-w-md text-center">
                  <Layers3 className="mx-auto h-12 w-12 text-muted-foreground" />
                  <h3 className="mt-4 text-xl font-bold">Nessun mazzo salvato</h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Crea o importa un mazzo prima di avviare una partita.
                  </p>
                  <a
                    href="/decks"
                    className="mt-5 inline-flex h-10 items-center justify-center gap-2 rounded bg-primary px-4 text-sm font-bold text-primary-foreground transition hover:bg-primary/90"
                  >
                    Vai ai mazzi
                    <ChevronRight className="h-4 w-4" />
                  </a>
                </div>
              </div>
            ) : filteredDecks.length === 0 ? (
              <div className="flex h-full items-center justify-center text-center text-muted-foreground">
                Nessun mazzo corrisponde alla ricerca.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {filteredDecks.map((deck) => {
                  const deckId = deck.id || deck.localId;
                  const active = selectedDeck && (selectedDeck.id || selectedDeck.localId) === deckId;
                  const domains = getDeckDomains(deck);
                  return (
                    <button
                      key={deckId}
                      type="button"
                      onClick={() => setSelectedDeckId(deckId)}
                      className={`min-h-32 border p-4 text-left transition ${
                        active
                          ? 'border-primary/80 bg-primary/10 shadow-lg shadow-primary/10'
                          : 'border-emerald-950/50 bg-[#0c100e]/70 hover:border-emerald-800/60 hover:bg-[#141b17]'
                      }`}
                    >
                      <div className="flex h-full flex-col justify-between gap-4">
                        <div>
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="truncate text-base font-bold">{deck.name || 'Mazzo senza nome'}</div>
                              <div className="mt-1 truncate text-sm text-muted-foreground">
                                {deck.legend?.name || 'Nessuna Legend'}
                              </div>
                            </div>
                            {active && <Swords className="h-5 w-5 shrink-0 text-primary" />}
                          </div>
                          {deck.chosenChampion && (
                            <div className="mt-2 truncate text-xs text-muted-foreground">
                              Champion: {deck.chosenChampion.name}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                          <div className="flex gap-2">
                            <span>{getDeckMainCount(deck)} main</span>
                            <span>{getDeckRuneCount(deck)} rune</span>
                            <span>{getDeckBattlefieldCount(deck)} BF</span>
                          </div>
                          {domains.length > 0 && (
                            <div className="flex max-w-[45%] gap-1 overflow-hidden">
                              {domains.map((domain) => (
                                <span key={domain} className="truncate border border-border/60 px-1.5 py-0.5 text-[10px] uppercase text-foreground/80">
                                  {domain}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <aside className="flex min-h-0 flex-col border border-emerald-950/60 bg-background shadow-xl shadow-black/20">
          <div className="border-b border-border/60 p-5">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">Preview</div>
            <h2 className="mt-2 font-display text-2xl font-bold">{selectedDeck?.name || 'Nessun mazzo'}</h2>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-5">
            {selectedDeck ? (
              <div className="space-y-5">
                <div className="overflow-hidden border border-emerald-950/60 bg-[#0c100e]/80">
                  {selectedDeckImage ? (
                    <img
                      src={selectedDeckImage}
                      alt={selectedDeck.legend?.name || selectedDeck.chosenChampion?.name || selectedDeck.name}
                      className="h-72 w-full object-contain p-2"
                    />
                  ) : (
                    <div className="flex h-64 items-center justify-center bg-muted/30 text-muted-foreground">
                      <Layers3 className="h-12 w-12" />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="border border-emerald-950/50 bg-[#0c100e]/75 p-3">
                    <div className="text-2xl font-bold">{getDeckMainCount(selectedDeck)}</div>
                    <div className="text-xs uppercase text-muted-foreground">Main</div>
                  </div>
                  <div className="border border-emerald-950/50 bg-[#0c100e]/75 p-3">
                    <div className="text-2xl font-bold">{getDeckRuneCount(selectedDeck)}</div>
                    <div className="text-xs uppercase text-muted-foreground">Rune</div>
                  </div>
                  <div className="border border-emerald-950/50 bg-[#0c100e]/75 p-3">
                    <div className="text-2xl font-bold">{getDeckBattlefieldCount(selectedDeck)}</div>
                    <div className="text-xs uppercase text-muted-foreground">BF</div>
                  </div>
                </div>

                <div className="space-y-3 text-sm">
                  <div className="flex items-start justify-between gap-4 border-b border-border/40 pb-3">
                    <span className="text-muted-foreground">Legend</span>
                    <span className="text-right font-semibold">{selectedDeck.legend?.name || 'Non impostata'}</span>
                  </div>
                  <div className="flex items-start justify-between gap-4 border-b border-border/40 pb-3">
                    <span className="text-muted-foreground">Champion</span>
                    <span className="text-right font-semibold">{selectedDeck.chosenChampion?.name || 'Non impostato'}</span>
                  </div>
                  <div className="flex items-start justify-between gap-4 border-b border-border/40 pb-3">
                    <span className="text-muted-foreground">Domini</span>
                    <span className="text-right font-semibold">
                      {selectedDeckDomains.length > 0 ? selectedDeckDomains.join(', ') : 'Non disponibili'}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex h-full items-center justify-center text-center text-muted-foreground">
                Seleziona un mazzo per vedere il riepilogo.
              </div>
            )}
          </div>

          <div className="border-t border-border/60 p-5">
            <button
              type="button"
              disabled={!selectedDeck || loading || !selectedMode.enabled}
              onClick={() => selectedDeck && selectedMode.enabled && handleStartBotGame(selectedDeck)}
              className="flex h-12 w-full items-center justify-center gap-2 rounded bg-primary px-4 text-sm font-bold text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
            >
              {loading ? 'Avvio partita...' : selectedMode.enabled ? `Gioca ${selectedMode.title}` : `${selectedMode.title} non disponibile`}
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
